import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateTeamId = (teamId) => {
    if (!teamId || typeof teamId !== 'string' || teamId.trim() === '') {
        throw new Error('Team ID is required and must be a valid string');
    }
    return teamId.trim();
};

const validateEmail = (email) => {
    if (!email || typeof email !== 'string' || email.trim() === '') {
        throw new Error('Email is required and must be a valid string');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
        throw new Error('Please enter a valid email address');
    }

    return email.trim().toLowerCase();
};

const validateRole = (role) => {
    if (!role || typeof role !== 'string' || role.trim() === '') {
        throw new Error('Role is required and must be a valid string');
    }

    if (!['admin', 'member'].includes(role.trim())) {
        throw new Error('Role must be either "admin" or "member"');
    }

    return role.trim();
};

const validateUserId = (userId) => {
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
        throw new Error('User ID is required and must be a valid string');
    }
    return userId.trim();
};

const validateSubteamId = (subteamId) => {
    if (subteamId !== null && subteamId !== undefined) {
        if (typeof subteamId !== 'string' || subteamId.trim() === '') {
            throw new Error('Subteam ID must be a valid string');
        }
        return subteamId.trim();
    }
    return null;
};

const validatePage = (page) => {
    if (page !== null && page !== undefined) {
        const pageNum = parseInt(page, 10);
        if (isNaN(pageNum) || pageNum < 1) {
            throw new Error('Page must be a positive integer');
        }
        return pageNum;
    }
    return 1;
};

const validateLimit = (limit) => {
    if (limit !== null && limit !== undefined) {
        const limitNum = parseInt(limit, 10);
        if (isNaN(limitNum) || limitNum < 1 || limitNum > 100) {
            throw new Error('Limit must be a positive integer between 1 and 100');
        }
        return limitNum;
    }
    return 50;
};

const validateInvitationToken = (token) => {
    if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Invitation token is required');
    }
    return token.trim();
};

// ============================================================================
// ERROR HANDLER
// ============================================================================

const handleTeamMemberApiError = (error, operation = 'Team Member API operation') => {
    console.error(`[TeamMemberAPI] ${operation} Error:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method
    });

    // Handle specific error cases
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to perform this action');
    }

    if (error.response?.status === 404) {
        const message = error.response?.data?.message;
        if (message && message.includes('invitation')) {
            throw new Error('Invitation not found or has expired');
        }
        throw new Error('Team member not found');
    }

    if (error.response?.status === 400) {
        const message = error.response?.data?.message;
        if (message && message.includes('already')) {
            throw new Error(message);
        }
        throw new Error('Invalid request. Please check your input.');
    }

    if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

// ============================================================================
// TEAM MEMBER OPERATIONS - /api/team-member/
// ============================================================================

/**
 * Invite a new team member
 * POST /api/team-member/:teamId/members/invite
 * @param {string} teamId - Team ID
 * @param {Object} memberData - { email, role?, subteamId?, isSubteamAdmin? }
 * @returns {Promise<Object>} Invitation data
 */
export const inviteTeamMember = async (teamId, memberData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validEmail = validateEmail(memberData.email);
        const validRole = validateRole(memberData.role || 'member');

        console.log('[TeamMemberAPI] Inviting team member:', validEmail, 'to team:', validTeamId);

        const requestData = {
            email: validEmail,
            role: validRole
        };

        // Optional subteam assignment
        if (memberData.subteamId) {
            requestData.subteamId = validateSubteamId(memberData.subteamId);
        }

        // Optional subteam admin flag
        if (memberData.isSubteamAdmin !== undefined) {
            requestData.isSubteamAdmin = Boolean(memberData.isSubteamAdmin);
        }

        // ✅ Optional send invites flag (defaults to true in backend)
        if (memberData.sendInvites !== undefined) {
            requestData.sendInvites = Boolean(memberData.sendInvites);
        }

        console.log('🟡 [TeamMemberAPI] Sending POST request...');
        const response = await apiClient.post(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/invite`,
            requestData
        );

        console.log('✅ [TeamMemberAPI] Response received:', response);
        console.log('[TeamMemberAPI] Team member invited successfully');
        return response.data;
    } catch (error) {
        console.error('❌ [TeamMemberAPI] Error inviting team member:', error);
        console.error('❌ [TeamMemberAPI] Error response:', error.response);
        console.error('❌ [TeamMemberAPI] Error response data:', error.response?.data);
        console.error('❌ [TeamMemberAPI] Error status:', error.response?.status);
        handleTeamMemberApiError(error, 'Inviting team member');
    }
};

/**
 * Create company pages (non-login team pages)
 * POST /api/team-member/:teamId/members/company-pages
 * @param {string} teamId - Team ID
 * @param {number} count - Number of pages to create
 * @returns {Promise<Object>} Created pages payload
 */
export const createCompanyPages = async (teamId, count) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const pageCount = Number(count);

        if (!Number.isInteger(pageCount) || pageCount < 1 || pageCount > 100) {
            throw new Error('Count must be an integer between 1 and 100');
        }

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/company-pages`,
            { count: pageCount }
        );

        return response.data;
    } catch (error) {
        handleTeamMemberApiError(error, 'Creating company pages');
    }
};

/**
 * Accept a team invitation
 * POST /api/team-member/invitations/:token/accept
 * @param {string} invitationToken - Invitation token from email
 * @returns {Promise<Object>} Accepted membership data
 */
export const acceptInvitation = async (invitationToken) => {
    try {
        const validToken = validateInvitationToken(invitationToken);

        console.log('[TeamMemberAPI] Accepting team invitation');

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team-member/invitations/${validToken}/accept`,
            { invitationToken: validToken }
        );

        console.log('[TeamMemberAPI] Team invitation accepted successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamMemberAPI] Error accepting team invitation:', error);
        handleTeamMemberApiError(error, 'Accepting team invitation');
    }
};

/**
 * Resend invitation to a pending member
 * POST /api/team-member/:teamId/members/:userId/resend
 * @param {string} teamId - Team ID
 * @param {string} userId - User ID of pending member
 * @returns {Promise<Object>} Resend confirmation
 */
export const resendInvitation = async (teamId, userId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validUserId = validateUserId(userId);

        console.log('[TeamMemberAPI] Resending invitation to:', validUserId);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/${validUserId}/resend`
        );

        console.log('[TeamMemberAPI] Invitation resent successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamMemberAPI] Error resending invitation:', error);
        handleTeamMemberApiError(error, 'Resending invitation');
    }
};

/**
 * Get team members list with pagination
 * GET /api/team-member/:teamId/members
 * @param {string} teamId - Team ID
 * @param {Object} options - { status?, role?, subteamId?, page?, limit? }
 * @returns {Promise<Object>} { members: [], pagination: {} }
 */
export const getTeamMembers = async (teamId, options = {}) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validPage = validatePage(options.page);
        const validLimit = validateLimit(options.limit);

        const params = new URLSearchParams({
            page: validPage.toString(),
            limit: validLimit.toString()
        });

        // Optional filters
        if (options.status) {
            params.append('status', options.status);
        }
        if (options.role) {
            params.append('role', validateRole(options.role));
        }
        if (options.subteamId !== undefined) {
            if (options.subteamId === 'none') {
                params.append('subteamId', 'none');
            } else if (options.subteamId) {
                params.append('subteamId', validateSubteamId(options.subteamId));
            }
        }

        console.log('[TeamMemberAPI] Getting team members:', validTeamId);

        const response = await apiClient.get(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members?${params}`
        );

        console.log('[TeamMemberAPI] Team members retrieved successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamMemberAPI] Error getting team members:', error);
        handleTeamMemberApiError(error, 'Getting team members');
    }
};

/**
 * Remove a team member
 * DELETE /api/team-member/:teamId/members/:userId
 * @param {string} teamId - Team ID
 * @param {string} userId - User ID to remove
 * @returns {Promise<Object>} Removal confirmation
 */
export const removeTeamMember = async (teamId, userId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validUserId = validateUserId(userId);

        console.log('[TeamMemberAPI] Removing team member:', validUserId, 'from team:', validTeamId);

        const response = await apiClient.delete(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/${validUserId}`
        );

        console.log('[TeamMemberAPI] Team member removed successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamMemberAPI] Error removing team member:', error);
        handleTeamMemberApiError(error, 'Removing team member');
    }
};

/**
 * Update member's role
 * PUT /api/team-member/:teamId/members/:userId/role
 * @param {string} teamId - Team ID
 * @param {string} userId - User ID
 * @param {string} role - New role ('admin' or 'member')
 * @returns {Promise<Object>} Updated member data
 */
export const updateMemberRole = async (teamId, userId, role) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validUserId = validateUserId(userId);
        const validRole = validateRole(role);

        console.log('[TeamMemberAPI] Updating member role:', validUserId, 'to:', validRole);

        const response = await apiClient.put(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/${validUserId}/role`,
            { role: validRole }
        );

        console.log('[TeamMemberAPI] Member role updated successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamMemberAPI] Error updating member role:', error);
        handleTeamMemberApiError(error, 'Updating member role');
    }
};

/**
 * Assign member to subteam (optional feature)
 * PUT /api/team-member/:teamId/members/:userId/subteam
 * @param {string} teamId - Team ID
 * @param {string} userId - User ID
 * @param {string|null} subteamId - Subteam ID or null to remove from subteam
 * @param {boolean} isSubteamAdmin - Whether user is subteam admin
 * @returns {Promise<Object>} Updated member data
 */
export const assignMemberToSubteam = async (teamId, userId, subteamId, isSubteamAdmin = false) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validUserId = validateUserId(userId);
        const validSubteamId = validateSubteamId(subteamId);

        console.log('[TeamMemberAPI] Assigning member to subteam:', validUserId);

        const response = await apiClient.put(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/${validUserId}/subteam`,
            {
                subteamId: validSubteamId,
                isSubteamAdmin: Boolean(isSubteamAdmin)
            }
        );

        console.log('[TeamMemberAPI] Member assigned to subteam successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamMemberAPI] Error assigning member to subteam:', error);
        handleTeamMemberApiError(error, 'Assigning member to subteam');
    }
};

export default {
    inviteTeamMember,
    createCompanyPages,
    acceptInvitation,
    resendInvitation,
    getTeamMembers,
    removeTeamMember,
    updateMemberRole,
    assignMemberToSubteam
};